import { computed, type Ref } from 'vue'
import { useAccess } from '@/ContextTab/stores/auth/useAccess'
import { formatUsername } from '@/utils/formatUsername'
import { useNewsItem } from './useNewsItem'
import { ENewsStatusLabel, ENewsStatusReverse, ENewsTypeReverse } from '@/lib/references'
import { useNewsStore } from '@/ContextTab/stores/news'
import type { TNewsId } from '@/ContextTab/stores/types'


export function useNewsItemVersion(newsItemId: Ref<TNewsId>) {

  const newsStore = useNewsStore()

  const version = computed(() => newsStore.news[newsItemId.value]?.version)

  const { userId } = useAccess()

  const { newsItemIsScheduled, newsItemIsRevoked } = useNewsItem(newsItemId)

  const getVersionUserName = computed(
    () =>
      formatUsername(version.value?.user?.name ?? '')
      || version.value?.recipient_group?.name
      || formatUsername(version.value?.recipient_user?.name ?? '')
      || '',
  )

  const getVersionAuthorsNames = computed(() =>
    (version.value?.authors ?? [])
      .map(({ user }) => formatUsername(user.name ?? '')),
  )

  const getVersionStatusDescription = computed(() => {
    if (!version.value?.status_id) {
      return ''
    }

    switch (version.value.status_id) {
      case ENewsStatusReverse.DRAFT:
        return getVersionUserName.value ? 'В работе: ' + getVersionUserName.value : 'В работе'
      case ENewsStatusReverse.WAITS_FOR_EDIT:
      case ENewsStatusReverse.ON_EDIT:
        return getVersionUserName.value
          ? (ENewsStatusLabel[version.value.status_id] + ': ' + getVersionUserName.value)
          : ENewsStatusLabel[version.value.status_id]
      case ENewsStatusReverse.WAITS_FOR_PUBLICATION:
        return newsItemIsScheduled.value
          ? 'Отложено'
          : ENewsStatusLabel[version.value.status_id]
      case ENewsStatusReverse.PUBLISHED:
        return newsItemIsRevoked.value
          ? 'Аннулирована'
          : ENewsStatusLabel[version.value.status_id]
      default:
        return ENewsStatusLabel[version.value.status_id]
    }
  })

  const isVersionOfTypeFlash = computed(
    () => version.value?.type_id === ENewsTypeReverse.FLASH,
  )

  const isVersionOfTypeExpress = computed(
    () => version.value?.type_id === ENewsTypeReverse.EXPRESS,
  )

  const getIsMyNewsVersion = computed(() =>
    version.value?.user?.id
      ? version.value.user.id === userId.value
      : false,
  )

  return {
    version,
    getVersionAuthorsNames,
    isVersionOfTypeFlash,
    isVersionOfTypeExpress,
    getIsMyNewsVersion,
    getVersionStatusDescription,
  }
}
